import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '../../../lib/apiAuth';
import { getSupabaseAdminClient } from '../../../lib/supabaseAdmin';

/**
 * Bank Movements API
 * GET /api/bank/movements - List movements with filters and pagination
 *
 * Query params:
 *   bank_account_id (required)
 *   fecha_desde, fecha_hasta - date range
 *   estado - conciliation status filter
 *   tipo - movement type filter
 *   min_importe, max_importe - amount range
 *   search - text search in concepto/referencia
 *   page (default 1), limit (default 50)
 */
async function handler(
  req: NextApiRequest,
  res: NextApiResponse,
  user: { id: string; email: string; roles: string[]; primaryRole: string }
) {
  if (req.method !== 'GET') {
    return res.status(405).json({ success: false, error: 'Method not allowed' });
  }

  const supabaseAdmin = getSupabaseAdminClient();

  try {
    const {
      bank_account_id,
      fecha_desde,
      fecha_hasta,
      estado,
      tipo,
      min_importe,
      max_importe,
      search,
      page: pageParam,
      limit: limitParam,
    } = req.query;

    if (!bank_account_id) {
      return res.status(400).json({ success: false, error: 'bank_account_id es obligatorio.' });
    }

    const page = Math.max(1, parseInt(pageParam as string, 10) || 1);
    const limit = Math.min(200, Math.max(1, parseInt(limitParam as string, 10) || 50));
    const offset = (page - 1) * limit;

    // ─── Build query ───
    let query = supabaseAdmin
      .from('bank_movements')
      .select('*', { count: 'exact' })
      .eq('bank_account_id', bank_account_id as string);

    // Date range filters
    if (fecha_desde) {
      query = query.gte('fecha_operacion', fecha_desde as string);
    }
    if (fecha_hasta) {
      query = query.lte('fecha_operacion', fecha_hasta as string);
    }

    // Status filter
    if (estado) {
      query = query.eq('estado_conciliacion', estado as string);
    }

    // Movement type filter
    if (tipo) {
      query = query.eq('tipo_movimiento', tipo as string);
    }

    // Amount range filters
    if (min_importe) {
      const minVal = parseFloat(min_importe as string);
      if (!isNaN(minVal)) {
        query = query.gte('importe', minVal);
      }
    }
    if (max_importe) {
      const maxVal = parseFloat(max_importe as string);
      if (!isNaN(maxVal)) {
        query = query.lte('importe', maxVal);
      }
    }

    // Text search in concepto and referencia
    if (search) {
      const searchStr = search as string;
      query = query.or(
        `concepto.ilike.%${searchStr}%,referencia.ilike.%${searchStr}%,concepto_complementario.ilike.%${searchStr}%`
      );
    }

    // Ordering and pagination
    query = query
      .order('fecha_operacion', { ascending: false })
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    const { data, error, count } = await query;

    if (error) {
      console.error('[API bank/movements] Error listing movements:', error);
      return res.status(500).json({ success: false, error: error.message });
    }

    return res.status(200).json({
      success: true,
      data,
      pagination: {
        page,
        limit,
        total: count ?? 0,
        totalPages: count ? Math.ceil(count / limit) : 0,
      },
    });
  } catch (error: any) {
    console.error('[API bank/movements] Unexpected error:', error);
    return res.status(500).json({
      success: false,
      error: error.message || 'Error inesperado al listar movimientos.',
    });
  }
}

export default withAuth(handler);
